import React, { useContext, useEffect, useRef, useState } from 'react';

import HeaderNavBarContext from '../context/HeaderNavBarContext';
import getFormattedMenuLinks from '../utils/getFormattedMenuLinks';
import { Link, MaskedLink } from '../../../components/Link';
import Box from '../../../components/Box/Box';
import Icon from '../../../components/Icon/Icon';
import { TMenuLink } from '../../../types/TMenuLink';
import { isArrowDown, isArrowUp, isEscape } from '../../../utils/keyboard';

type TUserDropdownMenu = {
  showHamburgerMenu: () => void;
  menuLinks?: TMenuLink[];
};

type TToggleHamburgerMenu = {
  showHamburgerMenu: () => void;
  userName: any;
};
function combineMenuAndSSOLinks(menuLinks: TMenuLink[] | undefined, ssoLinks: any) {
  return [
    ...getFormattedMenuLinks(menuLinks ?? []),
    {
      value: 'ssoLinks.logout.text',
      label: (
        <MaskedLink href={ssoLinks.logout.url} data-role={`mymp-${ssoLinks.logout.role}`} tabIndex={0}>
          {ssoLinks.logout.text}
        </MaskedLink>
      ),
    },
  ];
}

function isFocusOutsideDropdown(dropDownMenuRef: React.RefObject<HTMLDivElement | null>, event: FocusEvent): boolean {
  return Boolean(dropDownMenuRef.current && !dropDownMenuRef.current.contains(event.relatedTarget as Node));
}

function isValidKeyboardNavigation(isOpen: boolean, event: KeyboardEvent): boolean {
  return isOpen && (isArrowDown(event.key) || isArrowUp(event.key));
}

function getNextIndex(focusableElements: NodeListOf<HTMLElement>, event: KeyboardEvent) {
  // Find the currently focused element
  const currentIndex = Array.from(focusableElements).indexOf(document.activeElement as HTMLElement);
  const direction = isArrowDown(event.key) ? 1 : -1;
  return (currentIndex + direction + focusableElements.length) % focusableElements.length;
}

function getFocusableElements(dropDownMenuRef: React.RefObject<HTMLDivElement | null>): NodeListOf<HTMLElement> {
  return (dropDownMenuRef.current?.querySelectorAll<HTMLElement>(
    'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
  ) ?? []) as NodeListOf<HTMLElement>;
}

function hasFocusableElements(focusableElements: NodeListOf<HTMLElement> | undefined) {
  return focusableElements && focusableElements.length > 0;
}

function handleArrowNavigation(
  isOpen: boolean,
  event: KeyboardEvent,
  dropDownMenuRef: React.RefObject<HTMLDivElement | null>,
) {
  if (!isValidKeyboardNavigation(isOpen, event)) return;
  event.preventDefault();

  // Get all focusable elements inside the dropdown
  const focusableElements = getFocusableElements(dropDownMenuRef);
  if (!hasFocusableElements(focusableElements)) return;

  // Determine the next index based on the arrow key
  // Focus the next element
  focusableElements[getNextIndex(focusableElements, event)]?.focus();
}

function ToggleHamburgerMenu({ showHamburgerMenu, userName }: TToggleHamburgerMenu) {
  return (
    <li className="hz-hide-gte-lg">
      <Link
        role="button"
        aria-haspopup="true"
        aria-label={userName}
        data-role="hamburgerOpen"
        data-testid="hamburger-menu-button"
        tabIndex={0}
        onClick={showHamburgerMenu}
      >
        <Icon name="profile" color={'text-controls-default'} />
      </Link>
    </li>
  );
}

function UserProfileIcon() {
  return <Icon name="profile" color={'text-controls-default'} />;
}

function ToggleUserMenu({
  isOpen,
  setIsOpen,
  userName,
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  userName: any;
}) {
  return (
    <Link
      className="hz-Nav-dropdown-toggle"
      aria-expanded={isOpen}
      onClick={() => setIsOpen(!isOpen)}
      role="button"
      data-role="mymp-username"
      data-testid="user-dropdown-toggle"
      tabIndex={0}
    >
      <UserProfileIcon />
      <span className="hz-hide-lte-md">{userName}</span>
      <Icon name={isOpen ? 'arrow-up-regular' : 'arrow-down-regular'} />
    </Link>
  );
}

type DropdownMenuProps = {
  isOpen: boolean;
  options: {
    value: string;
    label: React.JSX.Element;
  }[];
};

function DropdownOptions({ isOpen, options }: Readonly<DropdownMenuProps>) {
  if (!isOpen) return null;

  function composeOptionKey(value: string, key: number): React.Key | null | undefined {
    return `${value}-${key}`;
  }

  return (
    <Box as="ul" parent="Nav-dropdown" name="menu" role="menu">
      {options.map(({ value, label, ...rest }, key) => (
        <Box {...rest} value={String(value)} key={composeOptionKey(value, key)} as="li" parent="Nav" name="pill">
          {label}
        </Box>
      ))}
    </Box>
  );
}

const UserDropdownMenu = ({ showHamburgerMenu, menuLinks }: TUserDropdownMenu) => {
  const {
    userDetails: { userName },
    ssoLinks,
  } = useContext<any>(HeaderNavBarContext);
  const [isOpen, setIsOpen] = useState(false);
  const dropDownMenuRef = useRef<HTMLDivElement>(null);

  const handleFocusOut = (event: FocusEvent) => {
    if (isFocusOutsideDropdown(dropDownMenuRef, event)) setIsOpen(false);
  };

  useEffect(() => {
    const dropDownMenuElement = dropDownMenuRef.current;
    dropDownMenuElement?.addEventListener('focusout', handleFocusOut, false);

    return () => dropDownMenuElement?.removeEventListener('focusout', handleFocusOut, false);
  }, [isOpen]);

  useEffect(() => {
    const escapeListener = (event: KeyboardEvent) => {
      if (isEscape(event.key)) {
        event.preventDefault();
        setIsOpen(false);
      }

      handleArrowNavigation(isOpen, event, dropDownMenuRef);
    };
    window?.addEventListener('keydown', escapeListener);
    return () => window?.removeEventListener('keydown', escapeListener);
  }, [isOpen]);

  return (
    <>
      <li className="hz-hide-lte-md">
        <Box parent="Nav" name="dropdown" as="div" modifiers={[isOpen ? 'open' : 'closed']} ref={dropDownMenuRef}>
          <ToggleUserMenu isOpen={isOpen} setIsOpen={setIsOpen} userName={userName} />
          <DropdownOptions isOpen={isOpen} options={combineMenuAndSSOLinks(menuLinks, ssoLinks)} />
        </Box>
      </li>

      <ToggleHamburgerMenu {...{ showHamburgerMenu, userName }} />
    </>
  );
};

export default UserDropdownMenu;
